import { useState } from 'react';
import { useActions, useSettings, useAgentParams } from '@/store/selectors';
import { Agent, NomogramSource, UnitSystem } from '@/types';
import { AGENT_PARAMS } from '@/config/agentParams';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { Collapsible } from '@/components/shared/Collapsible';
import { useAppLock, LockMenu } from '@/components/AppLock';
import { ExpressionInput, ScrubInput, Select, UnitToggle } from 'calc-controls';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import { nomogramLabel } from '@/utils/format';
import appIdentity from 'virtual:app-identity';

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

/** Settings screen: unit system, protocol, agent params, assay config, educational mode, app lock (TODO Task 1.30, 3.17). */
export function SettingsScreen(): JSX.Element {
  const settings = useSettings();
  const actions = useActions();
  const [showAbout, setShowAbout] = useState(false);
  const [confirmClearDemo, setConfirmClearDemo] = useState(false);

  return (
    <div className="screen" data-testid="settings-screen">
      <Breadcrumb />
      <h1 className="screen-title">Settings</h1>

      <ErrorBoundary label="General settings">
        <section className="panel">
          <h2 className="panel-title">General</h2>
          <UnitToggle
            label="Unit system"
            options={[
              { label: 'Conventional', value: UnitSystem.Conventional },
              { label: 'SI', value: UnitSystem.SI },
            ]}
            value={settings.unitSystem}
            onChange={(v) => actions.setUnitSystem(v as UnitSystem)}
          />
          <div className="field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.educationalMode}
                onChange={(e) => actions.setEducationalMode(e.target.checked)}
              />
              Educational mode (show equations & detailed rationale)
            </label>
          </div>
        </section>
      </ErrorBoundary>

      <ErrorBoundary label="App lock">
        <section className="panel">
          <h2 className="panel-title">App Lock</h2>
          <AppLockSection />
        </section>
      </ErrorBoundary>

      <ErrorBoundary label="Demo data">
        <section className="panel">
          <h2 className="panel-title">Demo Data</h2>
          <div className="field">
            <button
              type="button"
              className={`btn btn-sm ${confirmClearDemo ? 'btn-danger' : 'btn-secondary'}`}
              onClick={() => {
                if (confirmClearDemo) {
                  actions.clearDemoData();
                  setConfirmClearDemo(false);
                } else {
                  setConfirmClearDemo(true);
                }
              }}
              onBlur={() => setConfirmClearDemo(false)}
              data-testid="clear-demo-data"
            >
              {confirmClearDemo ? 'Confirm clear?' : 'Clear demo data'}
            </button>
            <div className="hint-text mt-1">
              Resets the calculator to its default state, removing any loaded demo dataset.
            </div>
          </div>
          <div className="field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.showLoadDemo}
                onChange={(e) => actions.setShowLoadDemo(e.target.checked)}
                data-testid="toggle-show-load-demo"
              />
              Show "Load Demo" button in header
            </label>
          </div>
        </section>
      </ErrorBoundary>

      <ErrorBoundary label="Protocol">
        <section className="panel">
          <h2 className="panel-title">Institutional Protocol</h2>
          <div className="field-row">
            <div className="field">
              <ScrubInput
                label="Renal EI cutoff"
                unit="mL/min"
                value={settings.protocol.renalCutoffMlMin}
                onChange={(v) =>
                  actions.setProtocol({ ...settings.protocol, renalCutoffMlMin: v })
                }
                min={0}
                max={120}
                decimals={0}
                inline
              />
            </div>
            <div className="field">
              <Select
                label="Default nomogram"
                options={Object.values(NomogramSource).map((n) => ({ value: n, label: nomogramLabel(n) }))}
                value={settings.protocol.defaultNomogram}
                onChange={(v) =>
                  actions.setProtocol({ ...settings.protocol, defaultNomogram: v as NomogramSource })
                }
              />
            </div>
          </div>
        </section>
      </ErrorBoundary>

      <Collapsible title="Agent PK parameters" defaultOpen={false}>
        <section className="panel">
          <AgentPKEditor />
        </section>
      </Collapsible>

      <Collapsible title="Assay configuration" defaultOpen={false}>
        <section className="panel">
          <div className="field-row">
            <div className="field">
              <ExpressionInput
                id="assay-lloq"
                label="LLOQ"
                unit="mg/L"
                value={settings.assayConfig.lloq}
                onChange={(v) =>
                  actions.setAssayConfig({ ...settings.assayConfig, lloq: v })
                }
                min={0}
                decimals={1}
                inline
              />
            </div>
            <div className="field">
              <label htmlFor="assay-unit">Unit label</label>
              <input
                id="assay-unit"
                type="text"
                value={settings.assayConfig.unitLabel}
                onChange={(e) =>
                  actions.setAssayConfig({ ...settings.assayConfig, unitLabel: e.target.value })
                }
              />
            </div>
          </div>
          <UnitToggle
            label="Undetectable handling"
            options={[
              { value: 'use-lloq-half', label: 'Use LLOQ/2' },
              { value: 'flag-only', label: 'Flag only' },
            ]}
            value={settings.assayConfig.undetectableHandling}
            onChange={(v) =>
              actions.setAssayConfig({
                ...settings.assayConfig,
                undetectableHandling: v as 'use-lloq-half' | 'flag-only',
              })
            }
          />
        </section>
      </Collapsible>

      <hr className="divider divider-lg" />

      <section className="panel">
        <h2 className="panel-title">About</h2>
        <div className="field">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowAbout(true)}
            data-testid="about-btn"
          >
            About this app
          </button>
        </div>
      </section>

      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        config={aboutConfig}
      />
    </div>
  );
}

function AppLockSection(): JSX.Element {
  const { lock, isEnabled, enableEncryption, decision } = useAppLock();

  if (decision === 'pending') {
    return (
      <div className="field">
        <p className="hint-text">
          App lock setup is pending. Restart the app to configure.
        </p>
      </div>
    );
  }

  if (isEnabled && lock) {
    return (
      <div className="field-row">
        <div className="field">
          <p className="hint-text">
            <strong>Encryption enabled.</strong> Patient data is protected with
            AES-256-GCM encryption. The app will lock automatically after 5
            minutes of inactivity.
          </p>
        </div>
        <div className="field">
          <LockMenu lock={lock} />
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      <p className="hint-text">
        No password protection is active. Enable app lock to encrypt stored
        patient data with a password.
      </p>
      <button
        type="button"
        className="applock-btn applock-btn-primary mt-2 maxw-200"
        onClick={enableEncryption}
      >
        Enable App Lock
      </button>
    </div>
  );
}

function AgentPKEditor(): JSX.Element {
  const actions = useActions();
  const currentParams = useAgentParams();
  const [selectedAgent, setSelectedAgent] = useState<Agent>(currentParams.agent);
  const params = AGENT_PARAMS[selectedAgent];

  return (
    <>
      <Select
        label="Agent"
        options={[
          { value: Agent.Gentamicin, label: 'Gentamicin' },
          { value: Agent.Tobramycin, label: 'Tobramycin' },
          { value: Agent.Amikacin, label: 'Amikacin' },
          { value: Agent.Plazomicin, label: 'Plazomicin' },
        ]}
        value={selectedAgent}
        onChange={(v) => setSelectedAgent(v as Agent)}
      />
      <div className="field-row">
        <div className="field">
          <ScrubInput
            label="EI dose"
            unit="mg/kg"
            value={params.eiDoseMgKg}
            onChange={(v) => actions.setPKParams(selectedAgent, { eiDoseMgKg: v })}
            min={0}
            max={15}
            step={0.1}
            decimals={1}
            inline
          />
        </div>
        <div className="field">
          <ScrubInput
            label="Vd"
            unit="L/kg"
            value={params.vdLkg}
            onChange={(v) => actions.setPKParams(selectedAgent, { vdLkg: v })}
            min={0}
            max={2}
            step={0.01}
            decimals={2}
            inline
          />
        </div>
      </div>
      <div className="hint-text">
        Editing these parameters overrides the institutional defaults for {selectedAgent}.
      </div>
    </>
  );
}
